import React from 'react';
import { motion, Variants } from 'framer-motion';

const LINE = 'rgba(245,245,242,0.22)';
const LINE_STRONG = 'rgba(245,245,242,0.4)';
const ACCENT = '#7C8CFF';

const draw: Variants = {
    hidden: { pathLength: 0, opacity: 0 },
    visible: (i: number) => ({
        pathLength: 1,
        opacity: 1,
        transition: { duration: 1, delay: 0.15 * i, ease: [0.22, 1, 0.36, 1] as const },
    }),
};

const NetworkVisual = () => (
    <svg viewBox="0 0 400 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
        {[
            [40, 60, 150, 40], [150, 40, 260, 100], [260, 100, 340, 60],
            [40, 60, 90, 180], [90, 180, 150, 40], [90, 180, 220, 220],
            [220, 220, 260, 100], [220, 220, 180, 320], [260, 100, 320, 260],
        ].map(([x1, y1, x2, y2], i) => (
            <motion.line
                key={i} x1={x1} y1={y1} x2={x2} y2={y2}
                stroke={LINE} strokeWidth={1}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                custom={i} variants={draw}
            />
        ))}
        {[[40, 60], [150, 40], [260, 100], [340, 60], [90, 180], [220, 220], [180, 320], [320, 260]].map(([cx, cy], i) => (
            <circle key={i} cx={cx} cy={cy} r={i === 5 ? 5 : 3} fill={i === 5 ? ACCENT : LINE_STRONG} />
        ))}
    </svg>
);

const ClusterVisual = () => {
    const dots = Array.from({ length: 42 }).map((_, i) => {
        const angle = (i / 42) * Math.PI * 2;
        const cluster = i % 5 === 0;
        const radius = cluster ? 30 + (i % 3) * 8 : 60 + (i % 7) * 22;
        return {
            x: 200 + Math.cos(angle * 3.1) * radius,
            y: 190 + Math.sin(angle * 2.3) * radius * 0.8,
            r: cluster ? 5 : 2.5,
            accent: cluster,
        };
    });
    return (
        <svg viewBox="0 0 400 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
            {dots.map((d, i) => (
                <motion.circle
                    key={i} cx={d.x} cy={d.y} r={d.r}
                    fill={d.accent ? ACCENT : LINE_STRONG}
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.01 * i }}
                />
            ))}
        </svg>
    );
};

const ArcsVisual = () => (
    <svg viewBox="0 0 400 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
        {[170, 130, 90, 50].map((r, i) => (
            <motion.circle
                key={r} cx={210} cy={210} r={r} fill="none"
                stroke={i === 3 ? ACCENT : LINE} strokeWidth={i === 3 ? 2 : 1}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                custom={i} variants={draw}
            />
        ))}
    </svg>
);

const GrowthVisual = () => {
    const bars = [40, 65, 52, 80, 70, 96];
    return (
        <svg viewBox="0 0 400 380" width="100%" height="100%" preserveAspectRatio="xMidYMid slice">
            <motion.line
                x1={40} y1={300} x2={360} y2={300} stroke={LINE} strokeWidth={1}
                initial="hidden" whileInView="visible" viewport={{ once: true }} variants={draw}
            />
            {bars.map((h, i) => (
                <motion.rect
                    key={i}
                    x={60 + i * 48}
                    width={26}
                    fill={i === bars.length - 1 ? ACCENT : LINE_STRONG}
                    initial={{ height: 0, y: 300 }}
                    whileInView={{ height: h * 2, y: 300 - h * 2 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                />
            ))}
        </svg>
    );
};

const visuals = [NetworkVisual, ClusterVisual, ArcsVisual, GrowthVisual];

const WorkVisual = ({ variant }: { variant: number }) => {
    const Visual = visuals[variant % visuals.length];
    return <Visual />;
};

export default WorkVisual;
